import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

const pillars = [
  { num: '01', icon: 'FiZap', accent: '#289199', title: 'The 1-Click Authority Protocol', short: 'Manual SEO is dead. We buried it.', body: 'Our engine autonomously audits your site and executes on-page optimization instantly — removing every friction point of technical SEO.' },
  { num: '02', icon: 'FiCpu', accent: '#eb5e55', title: 'AI Search Dominance', short: 'Google is just the beginning.', body: 'Your customers are asking ChatGPT, Claude, Perplexity, and Gemini. We optimize your architecture so AI models can discover and cite your business.' },
  { num: '03', icon: 'FiTool', accent: '#f59e0b', title: 'The Invisible Webmaster', short: 'No tech team. No plugin chaos.', body: 'One simple integration connects to your site. The engine continuously detects and repairs broken links, wipes out site errors, and builds smart internal linking.' },
];

const aiPlatforms = [
  { name: 'ChatGPT', color: '#10a37f' },
  { name: 'Claude', color: '#c96442' },
  { name: 'Perplexity', color: '#6b7aed' },
  { name: 'Gemini', color: '#4285f4' },
];

const capabilities = [
  { icon: 'FiTag', label: 'Title Tag Optimization', sub: 'Auto-written for max CTR' },
  { icon: 'FiFileText', label: 'Meta Descriptions', sub: 'Data-driven, always fresh' },
  { icon: 'FiImage', label: 'Image Alt Text', sub: 'Keyword-injected at scale' },
  { icon: 'FiLink', label: 'Internal Link Builder', sub: 'Smart structure, zero effort' },
  { icon: 'FiAlertCircle', label: 'Broken Link Repair', sub: 'Auto-detected & fixed' },
  { icon: 'FiCode', label: 'Schema Markup', sub: 'AI-readable structured data' },
];

function PillarCard({ pillar, index }) {
  const [open, setOpen] = useState(false);
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: index * 0.1 }} className="group relative flex flex-col p-4 sm:p-5 rounded-2xl bg-[#181818] border border-white/5 overflow-hidden">
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none rounded-2xl" style={{ boxShadow: `inset 0 0 0 1px ${pillar.accent}40, 0 0 30px ${pillar.accent}10` }} />
      <div className="relative z-10 flex flex-col h-full gap-2">
        <div className="flex items-center justify-between">
          <div className="h-9 w-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${pillar.accent}20`, border: `1px solid ${pillar.accent}40` }}>
            <SafeIcon name={pillar.icon} className="h-4 w-4" style={{ color: pillar.accent }} />
          </div>
          <span className="text-3xl font-michroma text-white/5">{pillar.num}</span>
        </div>
        <span className="text-[10px] font-black uppercase tracking-[0.18em]" style={{ color: pillar.accent }}>{pillar.short}</span>
        <h3 className="text-xs sm:text-sm font-michroma text-white leading-snug">{pillar.title}</h3>
        <AnimatePresence>
          {open && (
            <motion.p initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.3 }} className="text-xs text-white/50 leading-relaxed overflow-hidden">{pillar.body}</motion.p>
          )}
        </AnimatePresence>
        <button onClick={() => setOpen(!open)} className="self-start flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest mt-auto" style={{ color: pillar.accent }}>
          {open ? 'Collapse' : 'Learn More'}
          <SafeIcon name={open ? 'FiChevronUp' : 'FiChevronDown'} className="h-3 w-3" />
        </button>
      </div>
    </motion.div>
  );
}

export default function OmnipresenceEngine() {
  return (
    <section className="relative w-full min-h-full bg-[#181818] flex flex-col justify-center py-24 px-4 sm:px-6 md:px-12 overflow-hidden font-lexend">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-teal/40 to-transparent" />
      
      <div className="relative z-10 max-w-6xl mx-auto w-full">
        <motion.div initial={{ opacity: 0, y: -20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }} className="text-center mb-5 sm:mb-8">
          <span className="inline-block text-brand-teal font-bold text-xs uppercase tracking-[0.2em] mb-3">Autopilot SEO Alignment</span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-michroma text-white tracking-tight leading-tight">
            The <span className="text-brand-teal drop-shadow-[0_0_20px_rgba(40,145,153,0.35)]">Omnipresence</span> Engine
          </h2>
          <p className="mt-3 text-white/55 text-xs sm:text-sm max-w-xl mx-auto leading-relaxed">
            Your website is likely speaking a language that search algorithms ignore. <span className="text-white/80 font-semibold">If your technical foundation is out of alignment, you remain invisible.</span>
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-5">
          {pillars.map((p, i) => <PillarCard key={i} pillar={p} index={i} />)}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-4">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="rounded-2xl border border-white/5 bg-[#202020] p-4 sm:p-5">
            <p className="text-[10px] font-black uppercase tracking-[0.25em] text-brand-teal mb-3 text-center">Optimized to Be Cited By</p>
            <div className="flex flex-wrap items-center justify-center gap-2">
              {aiPlatforms.map((platform, i) => (
                <div key={i} className="flex items-center gap-2 px-3 py-1.5 rounded-full border" style={{ backgroundColor: `${platform.color}12`, borderColor: `${platform.color}30` }}>
                  <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: platform.color }} />
                  <span className="text-[11px] font-bold text-white/70">{platform.name}</span>
                </div>
              ))}
              <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/5 bg-white/5">
                <SafeIcon name="FiSearch" className="h-3 w-3 text-white/30" />
                <span className="text-[11px] font-bold text-white/30">+ Traditional Search</span>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.1 }} className="rounded-2xl border border-white/5 bg-[#202020] p-4 sm:p-5">
            <p className="text-[10px] font-black uppercase tracking-[0.25em] text-white/25 mb-3 text-center">What The Engine Handles</p>
            <div className="grid grid-cols-2 gap-2">
              {capabilities.map((item, i) => (
                <div key={i} className="flex items-center gap-2 p-2 rounded-xl bg-[#181818] border border-white/5">
                  <div className="h-7 w-7 rounded-lg bg-brand-teal/10 border border-brand-teal/20 flex items-center justify-center flex-shrink-0">
                    <SafeIcon name={item.icon} className="h-3 w-3 text-brand-teal" />
                  </div>
                  <div>
                    <p className="text-[10px] font-michroma text-white leading-tight">{item.label}</p>
                    <p className="text-[9px] text-white/35">{item.sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="rounded-2xl border border-brand-teal/15 bg-[#202020] p-4 sm:p-5 text-center">
          <p className="text-[10px] font-black uppercase tracking-[0.25em] text-brand-teal mb-2">The Transformation</p>
          <p className="text-white/55 text-xs leading-relaxed max-w-2xl mx-auto">
            You stop guessing what search engines want. <span className="text-white/80 font-semibold">You supply the vision and the words. The AI handles the invisible math.</span> Your website becomes a highly tuned, automated traffic magnet — working for you around the clock.
          </p>
        </motion.div>
      </div>
    </section>
  );
}